import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { publications } from '../data/publicationsData';

const pickLang = (obj, lang) => {
  if (obj == null) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj.fr || obj.en || '';
};

const t = (lang, fr, en) => (lang === 'fr' ? fr : en);

const PublicationsPage = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-[240px] pb-20" data-testid="publications-page">
        {/* Hero */}
        <section className="relative h-[380px] md:h-[460px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/tk5ilrvv_IMG_6330.jpg')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <span className="inline-block text-orange-400 font-semibold tracking-widest text-sm mb-3">
              {t(language, 'GLOBAL CONSTRUCTION & INGÉNIERIE', 'GLOBAL CONSTRUCTION & INGÉNIERIE')}
            </span>
            <h1
              className="text-white text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-3xl"
              data-testid="publications-title"
            >
              {t(language, 'Publications', 'Publications')}
            </h1>
            <div className="mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {t(
                language,
                "Retour sur nos chantiers, cas d'usage et analyses métier. Le point de vue de nos ingénieurs et architectes sur la construction à Kinshasa.",
                'Insights from our sites, case studies and industry analyses. The perspective of our engineers and architects on construction in Kinshasa.'
              )}
            </p>
          </div>
        </section>

        {/* List */}
        <section className="py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {publications.map((pub) => {
                const dateStr = new Date(pub.date).toLocaleDateString(
                  language === 'fr' ? 'fr-FR' : 'en-GB',
                  { day: 'numeric', month: 'long', year: 'numeric' }
                );
                return (
                  <Link
                    key={pub.slug}
                    to={`/actualites/${pub.slug}`}
                    className="group block bg-white border border-gray-200 rounded-sm overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                    data-testid={`publication-card-${pub.slug}`}
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={pub.cover}
                        alt={pickLang(pub.title, language)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <span
                        className="absolute top-3 left-3 inline-block px-3 py-1 rounded-sm font-bold tracking-widest uppercase text-white text-xs"
                        style={{ backgroundColor: '#FF8C00' }}
                      >
                        {pickLang(pub.tag, language)}
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" /> {dateStr}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5" /> {pub.location}
                        </span>
                      </div>
                      <h2 className="text-lg md:text-xl font-bold text-gray-900 leading-snug mb-4 group-hover:text-[#702963] transition-colors">
                        {pickLang(pub.title, language)}
                      </h2>
                      <div className="inline-flex items-center gap-2 text-sm font-semibold text-orange-500 group-hover:gap-3 transition-all">
                        {t(language, 'Lire la publication', 'Read the publication')}
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PublicationsPage;
